<script lang="ts" setup>
import SelectInput from '@/corePagesModule/SelectInput/SelectInput.vue';
import { useGroupListSelectInputStore } from './useGroupListSelectInputStore';
import { storeToRefs } from 'pinia';
import { computed, onMounted, watch } from 'vue';
import { i18n } from '@/i18n';

const props = withDefaults(
  defineProps<{
    selectInputIndex: number;
    storeTag: string;
    selectedGroupIdProp?: string;
    showLabel?: boolean;
    useReset?: boolean;
    reload?: boolean;
    useTeleport?: boolean;
  }>(),
  {
    selectedGroupIdProp: undefined,
    useReset: true,
  }
);

const emits = defineEmits<{
  (e: 'selectedGroup', groupId?: string): void;
  (e: 'selectedGroupName', groupName?: string): void;
}>();

const t = i18n.t;

const store = useGroupListSelectInputStore(props.storeTag)();
const { state } = storeToRefs(store);

onMounted(() => {
  store.init();
  if (props.selectedGroupIdProp) {
    state.value.selectedGroupId = props.selectedGroupIdProp;
  }
});

watch(
  () => props.selectedGroupIdProp,
  (newVal) => {
    state.value.selectedGroupId = newVal;
  }
);

watch(
  () => props.reload,
  async (newVal) => {
    if (newVal) {
      await store.reloadGroupData();
    }
  }
);

/**
 * グループ名を選択した際の処理
 * @param index - 選択したグループのインデックス
 */
const onSelectedGroup = (index: number) => {
  store.onSelectedGroup(index);
  emits('selectedGroup', state.value.selectedGroupId);
  emits('selectedGroupName', store.selectedGroupName);
};

/**
 * 外すボタンを押した際の処理
 * @param value - 選択したグループ名
 */
const onUpdatedValue = (value: string) => {
  if (value === '') {
    store.onSelectedDefaultValue();
    emits('selectedGroup');
    emits('selectedGroupName');
  }
};

/**
 * セレクトボックスをクリックした際の処理
 */
const onClickSelectInput = async () => {
  await store.reloadGroupData();
  state.value.selectInputState = true;
};

const useLabel = computed(() => (props.showLabel ? t('グループ') : ''));
</script>

<template>
  <SelectInput
    :index="selectInputIndex"
    :model-value="store.selectedGroupName"
    :placeholder="t('グループ名')"
    :label="useLabel"
    :filter-type="'AGGREGATE_BUSINESS_GROUP'"
    :use-reset="useReset"
    :is-open-prop="state.selectInputState"
    :prop-options="store.groupListNames"
    :prop-paging-state="state.pagingState"
    :prop-paging-options="state.pagingOptions"
    :use-teleport="useTeleport"
    @click:select-input="() => onClickSelectInput()"
    @close="() => (state.selectInputState = false)"
    @update:model-value="(value) => onUpdatedValue(value)"
    @update:value-with-index="(_, index) => onSelectedGroup(index)"
  />
</template>
